import { useMemo } from 'react';

import { useMerchant } from '@contexts/operator/MerchantContext';

import operatorApiFetch from '@services/operator/apiService';

/**
 * Operator ReturnOrder Service Hook
 *
 * 接收 merchantId 作為參數，並注入到所有 API 呼叫中
 *
 * @param {Object} [options] - 選項
 * @param {string|null} [options.merchantId] - Merchant ID（可選，如果未提供則從 MerchantContext 獲取）
 *
 * @example
 * const returnOrderService = useOperatorReturnOrderService({ merchantId: 123 });
 *
 * // merchant_id 會自動加入到 request
 * await returnOrderService.create({ name: 'RO001', order_id: 1, status: 'waiting' });
 */
export function useOperatorReturnOrderService(options = {}) {
  const merchantContext = useMerchant();
  const contextMerchantId = merchantContext?.merchantId;

  const merchantId = options.merchantId ?? contextMerchantId;  // 傳入優先，否則用 context

  return useMemo(() => ({
    // 獲取退貨單列表
    getRecords: async (params = {}) => {
      const res = await operatorApiFetch('return_orders/get_records', {
        method: 'POST',
        body: {
          ...params,
          merchant_id: merchantId
        }
      });
      return res.json();
    },

    // 獲取所有退貨單 ID
    getIds: async (params = {}) => {
      const res = await operatorApiFetch('return_orders/get_ids', {
        method: 'POST',
        body: {
          ...params,
          merchant_id: merchantId
        }
      });
      return res.json();
    },

    // 獲取單個退貨單
    get: async (id) => {
      const res = await operatorApiFetch(`return_orders/${id}`, {
        method: 'GET'
      });
      return res.json();
    },

    // 獲取單個退貨單（別名）
    getById: async (id) => {
      const res = await operatorApiFetch(`return_orders/${id}`, {
        method: 'GET'
      });
      return res.json();
    },

    // 取得建議單號（自動帶入 merchant_id）
    suggestName: async () => {
      const queryParams = new URLSearchParams();
      if (merchantId) queryParams.append('merchant_id', merchantId);
      const qs = queryParams.toString();
      const res = await operatorApiFetch(`return_orders/suggest_name${qs ? `?${qs}` : ''}`, {
        method: 'GET'
      });
      return res.json();
    },

    // 創建退貨單（自動注入 merchant_id）
    create: async (data) => {
      const res = await operatorApiFetch('return_orders', {
        method: 'POST',
        body: {
          merchant_id: merchantId,
          return_order: {
            ...data,
          }
        }
      });
      return res.json();
    },

    // 更新退貨單
    update: async (id, data) => {
      const res = await operatorApiFetch(`return_orders/${id}`, {
        method: 'PUT',
        body: {
          return_order: {
            ...data,
          }
        }
      });
      return res.json();
    },

    // 刪除退貨單
    delete: async (id) => {
      const res = await operatorApiFetch(`return_orders/${id}`, {
        method: 'DELETE'
      });
      return res.json();
    },

    // 驗證退貨
    verify: async (id) => {
      const res = await operatorApiFetch(`return_orders/${id}/verify`, {
        method: 'POST'
      });
      return res.json();
    },

    // 完成入庫
    store: async (id) => {
      const res = await operatorApiFetch(`return_orders/${id}/store`, {
        method: 'POST'
      });
      return res.json();
    },

    // 取消退貨單
    cancel: async (id) => {
      const res = await operatorApiFetch(`return_orders/${id}/cancel`, {
        method: 'POST'
      });
      return res.json();
    },

    // 恢復退貨單
    resume: async (id) => {
      const res = await operatorApiFetch(`return_orders/${id}/resume`, {
        method: 'POST'
      });
      return res.json();
    },

    // 獲取訂單選項（用於 Select）
    getOrders: async ({ search, sinceId, pageSize } = {}) => {
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);
      if (sinceId) queryParams.append('since_id', sinceId);
      if (pageSize) queryParams.append('page_size', pageSize);
      if (merchantId) queryParams.append('merchant_id', merchantId);

      const res = await operatorApiFetch(`orders?${queryParams.toString()}`, {
        method: 'GET'
      });
      const response = await res.json();

      if (response.success && response.data) {
        return {
          options: response.data.map(order => ({
            label: order.name || `Order #${order.id}`,
            value: order.id
          })),
          hasMore: response.has_more,
          sinceId: response.since_id
        };
      }
      return { options: [], hasMore: false, sinceId: null };
    },

    // 獲取單個訂單詳情 (for pre-filling items)
    getOrder: async (id) => {
      const res = await operatorApiFetch(`orders/${id}`, {
        method: 'GET'
      });
      return res.json();
    },

    // 獲取產品選項（用於 Select）
    getProducts: async (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      queryParams.append('limit', params.limit || 20);
      queryParams.append('merchant_id', merchantId);

      const res = await operatorApiFetch(`products?${queryParams.toString()}`, {
        method: 'GET'
      });
      const response = await res.json();

      if (response.success && response.data) {
        // 將產品轉換為 Select 選項格式
        return {
          options: response.data.map(product => ({
            label: product.sku + ' - ' + product.name,
            value: product.id,
            sku: product.sku,
            name: product.name,
            validity_days: product.validity_days
          })),
          hasMore: false, // Simple fetch, assume no more for now or check pagination
          sinceId: null
        };
      }
      return { options: [], hasMore: false, sinceId: null };
    },

    // 獲取狀態選項
    getStatuses: async () => {
      return ['pending', 'verified', 'stored', 'canceled'];
    }
  }), [merchantId]);
}
